import { ProcurementType } from '@/types';
import dayjs from 'dayjs';
import z from 'zod';

export const newPoLineRowSchema = z
  .object({
    part_type: z.enum(['system', 'manual']),
    part: z.preprocess((value) => {
      if (
        value === '' ||
        value === null ||
        value === undefined ||
        value === 0
      ) {
        return undefined;
      }
      const number = Number(value);
      return Number.isNaN(number) ? undefined : number;
    }, z.number().positive().optional()),
    manual_part_number: z.string().optional(),
    description: z.string().optional(),
    qty: z.coerce.number().int().positive({
      message: 'Quantity must be a positive whole number',
    }),
    unit_price: z
      .union([z.string(), z.number()])
      .transform((value) => (typeof value === 'number' ? String(value) : value))
      .optional(),
    needed_by: z
      .string()
      .refine((date) => date === '' || dayjs(date).isValid(), {
        message: 'Select a valid date',
      })
      .optional(),
    promised_date: z
      .string()
      .refine((date) => date === '' || dayjs(date).isValid(), {
        message: 'Select a valid date',
      })
      .optional(),
    notes: z.string().optional(),
    release: z.preprocess(
      (value) =>
        value === '' || value === null || value === undefined
          ? undefined
          : Number(value),
      z.number().positive().optional(),
    ),
  })
  .refine(
    (line) =>
      line.part_type !== 'system' || (line.part != null && line.part > 0),
    { message: 'Part is required', path: ['part'] },
  )
  .refine(
    (line) =>
      line.part_type !== 'manual' ||
      (line.manual_part_number != null &&
        line.manual_part_number.trim() !== ''),
    {
      message: 'Item / service is required',
      path: ['manual_part_number'],
    },
  );

export const newPoLinesFormSchema = z.object({
  newLines: z.array(newPoLineRowSchema).min(1, {
    message: 'Add at least one line before saving',
  }),
});

export type NewPoLinesFormType = z.infer<typeof newPoLinesFormSchema>;

export const buildAdditionalPurchaseOrderLinesPayload = (
  rows: NewPoLinesFormType['newLines'],
): ProcurementType.PurchaseOrderLines.Post['body'] =>
  rows.map((line) => ({
    qty: line.qty,
    ...(line.unit_price !== undefined &&
      line.unit_price !== '' && { unit_price: line.unit_price }),
    ...(line.needed_by ? { needed_by: line.needed_by } : {}),
    ...(line.promised_date ? { promised_date: line.promised_date } : {}),
    ...(line.notes !== undefined && { notes: line.notes }),
    ...(line.part_type === 'system' && line.release
      ? { release: line.release }
      : {}),
    ...(line.part_type === 'system'
      ? { part: line.part as number }
      : {
          manual_part_number: (line.manual_part_number ?? '').trim(),
          description: line.description?.trim() || null,
        }),
  }));
